import { withoutDanglingMarkdown } from "./danglingMarkdown";
import { useSub } from "../subscription/useSub";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAllowsFency } from "../ai/useAllowsFency";
import { useAiSkillLabels } from "../ai/useAiSkillLabels";
import { creationProgressNow, pageEntityName, refreshCreationProgress, wizardStep } from "./pageEntityNames";
import { useLocation, useNavigate } from "react-router-dom";
import type { NavigateFunction, NavigateOptions, To } from "react-router-dom";
import { isAiNotAllowed } from "../ai/aiNotAllowed";
import { ASSISTANT_NOT_INCLUDED_NOTE, isAssistantNotIncluded } from "./assistantAccess";
import { contentFilteredNote, isContentFiltered } from "./contentFiltered";
import { allToolNames, enabledToolNames, wireToolSchemas } from "./enabledTools";
import { bffApi } from "../../autogen/bff-api";
import type { AgentConversationDto, AgentConversationSummaryDto, EventListItemDto } from "../../autogen/bff-api";
import { useApiError } from "../errors/useApiError";
import { newestFirst, withOlderPage, type OlderConversations } from "./conversationPages";
import { useEventNavigation } from "../sourcing-events/useEventNavigation";
import { urls } from "../../urls";
import { useBaseData } from "../useBaseData";
import { ddRum } from "../../datadog";
import { useLoggedInWithOrgContextState } from "../auth/useLoggedInWithOrgContextState";
import { useRunsPublicProcurements } from "../organization/useRunsPublicProcurements";
import { useAppDispatch } from "../redux/hooks";
import { createAgentMemory } from "./agentMemory";
import type { WireMessage } from "./agentMemory";
import { streamAgentTurn } from "./agentStream";
import type { StreamHandlers } from "./agentStream";
import type { AgentTurn, AuditedValues, ConfirmDecision, DocumentSource } from "./agentTypes";
import { streamAnswer } from "./answerStream";
import { withAiResult } from "./aiResultHandoff";
import { useHandoffStartsConversation } from "./useHandoffStartsConversation";
import { usePageRequestStartsConversation } from "./usePageRequestStartsConversation";
import { useAskedQuestionIsSent } from "./useAskedQuestionIsSent";
import { useAssistantPanel } from "./assistantPanelContext";
import { changesData, executeCall, resolveEntityNavigation, stepLabelFor } from "./callHandlers";
import { currentToolName } from "./retiredToolNames";
import type { CallDeps } from "./callHandlers";
import { entityLabel } from "./confirmationCardFormatting";
import { conversationSubjectContext, resolvePageContext, withPageContext } from "./pageContext";
import {
  indexedEntityTitle,
  subjectAwayFromPage,
  subjectOfPage,
  withKnownName,
  type AnsweredOn,
  type ConversationSubject,
} from "./conversationSubject";
import { documentScopeContext } from "./documentScope";
import type { PendingConfirmation } from "./ConfirmationCard";
import {
  buildClientState,
  createConversation,
  deleteConversation,
  getConversation,
  listOlderConversations,
  parseClientState,
  restoreEntityIndex,
  saveConversation,
  serializeClientState,
  toRestoredTurn,
  snapshotOfTurnInFlight,
  updateConversation,
  updateConversationWithoutListRefresh,
} from "./conversationStore";
import type { ClientState, ConversationSaveTriggers } from "./conversationStore";
import { toResumableConversation } from "./resumableConversation";
import { filterableCustomContractFields } from "./contractReportQuery";
import { buildDraftSystemPrompt, buildSystemPrompt } from "./systemPrompt";
import { SEARCH_TOOL_NAME, toError } from "./tools";
import type { GenerateDraft, ToolContext, ToolResult } from "./tools";
import { fitsPlanBudget, settleHistoryForTurn } from "./conversationCondensing";
import { runTurnLoop } from "./turnLoop";
import { endsWithQuestionWithoutCard } from "./answerChoices";
import { useListStatusLabel } from "./listStatusLabels";
import { streamCompletion } from "./streamCompletion";
import { useAgentAudit } from "./useAgentAudit";
import { useAgentSession } from "./useAgentSession";
import { useDocumentSearch } from "./useDocumentSearch";
import { defaultDepartment } from "../departments/defaultDepartment";
import {
  contractDepartmentOptions,
  departmentsForNewEvent,
  initialProjectDepartmentId,
  projectDepartmentOptions,
} from "../departments/eventCreationDepartments";
import { organizationHasSeveralDepartments } from "../departments/departmentVisibility";
import { withoutToolNames } from "./toolNames";
import {
  aiSettingRefusalNote,
  readsConversationFiles,
  withAttachments,
  type ConversationAttachment,
} from "./conversationFiles";
import { projectOfNewProcurement } from "./createProcurementParams";
import { createsProcurementWithAi } from "./fullAssistantPage";
import { emptyTurnProgress, stopSummary } from "./stopSummary";

export const CARD_CORRECTION =
  "[Systemet, ikke brukeren] Svaret ditt i «Opprett med KI» slutter med et spørsmål uten valgblokk, så brukeren " +
  "har ingenting å klikke på. Skriv svaret på nytt med samme innhold, og still spørsmålet til slutt som en valgblokk. " +
  "Spør du om å gå videre, er alternativene å gå videre til det neste området som gjenstår, med navnet på området, " +
  "og å justere det som nettopp ble lagret.";

const SAVE_DEBOUNCE_MS = 1_000;
const IN_FLIGHT_SAVE_INTERVAL_MS = 5_000;

const MAX_STEPS = 25;
const PLAN_IDLE_TIMEOUT_MS = 60_000;
const ANSWER_IDLE_TIMEOUT_MS = 90_000;
const PROGRESS_REFRESH_TIMEOUT_MS = 3_000;

const settlesAfter = (ms: number, signal: AbortSignal | undefined) =>
  new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        resolve();
      },
      { once: true },
    );
  });

export const PLAN_FAILED_NOTE = "Assistenten kunne ikke svare.";

export const offeredModelOrDefault = (
  modelId: string | null,
  offered: { id: string }[] | undefined,
): string | null => (modelId !== null && offered && !offered.some((model) => model.id === modelId) ? null : modelId);

const CONVERSATION_PAGE_SIZE = 50;

const NO_CONVERSATIONS: AgentConversationSummaryDto[] = [];


type SaveSnapshot = {
  conversationId: string | null;
  generation: number;
  turnInFlight: boolean;
  messages: WireMessage[];
  clientState: ClientState;
  signature: string;
  unchanged: boolean;
};

type PageAddress = { pathname: string; search: string };
type TurnPage = { address: PageAddress; onAssistantMove: () => void };
const addressOf = ({ pathname, search }: PageAddress): PageAddress => ({ pathname, search });

const conversationSignature = (messages: WireMessage[], clientState: ClientState): string =>
  JSON.stringify({ messages, clientState: serializeClientState(clientState) });

export const useAssistant = () => {
  const authState = useLoggedInWithOrgContextState();
  const dispatch = useAppDispatch();
  const { trigger: triggerApiError } = useApiError();
  const navigate = useNavigate();
  const location = useLocation();
  const { panelOpen, openPanel, handoff, takeHandoff, documentScope, pageRequest, question } = useAssistantPanel();
  const documentScopeRef = useRef(documentScope);
  documentScopeRef.current = documentScope;
  const navigateToEvent = useEventNavigation();
  const statusLabel = useListStatusLabel();
  const entityIndex = useRef(new Map<string, Record<string, unknown>>());
  const locationRef = useRef(location);
  locationRef.current = location;
  const turnPageRef = useRef<TurnPage | null>(null);

  const organizationId = authState.selectedOrg.id;
  const personId = authState.me.id;
  const departmentId = defaultDepartment(authState.selectedOrg)?.id ?? "";
  const eventDepartments = departmentsForNewEvent(authState.selectedOrg).map((d) => ({ id: d.id, name: d.name }));
  const contractDepartments = organizationHasSeveralDepartments(authState.selectedOrg)
    ? contractDepartmentOptions(authState.selectedOrg).map((d) => ({ id: d.id, name: d.name }))
    : [];
  const projectDepartments = organizationHasSeveralDepartments(authState.selectedOrg)
    ? projectDepartmentOptions(authState.selectedOrg).map((d) => ({ id: d.id, name: d.name }))
    : [];
  const defaultProjectDepartmentId = initialProjectDepartmentId(authState.selectedOrg);
  const buyerRunsPublicProcurements = useRunsPublicProcurements();
  const buyerRegulation = authState.selectedOrg.defaultRegulation ?? null;
  const regulationPendingRef = useRef(false);
  const setRegulationPending = useCallback((pending: boolean) => {
    regulationPendingRef.current = pending;
  }, []);
  const baseData = useBaseData();
  const norwayCountryId = baseData.countries.find((country) => country.alpha3Code === "NOR")?.id ?? "";

  const leaveAssistantPageIntoPanel = useCallback(
    (go: () => void) => {
      const leavesAssistantPage = locationRef.current.pathname === urls.app.assistant;
      go();
      // BrowserRouter writes the address at once, while useLocation only catches up on the next render.
      locationRef.current = { ...locationRef.current, pathname: window.location.pathname, search: window.location.search };
      const turnPage = turnPageRef.current;
      if (turnPage) {
        turnPage.address = addressOf(locationRef.current);
        turnPage.onAssistantMove();
      }
      if (leavesAssistantPage) openPanel();
    },
    [openPanel],
  );
  const assistantNavigate = useCallback<NavigateFunction>(
    (to: To | number, options?: NavigateOptions) =>
      leaveAssistantPageIntoPanel(() => (typeof to === "number" ? navigate(to) : navigate(to, options))),
    [leaveAssistantPageIntoPanel, navigate],
  );
  const assistantNavigateToEvent = useCallback(
    (event: EventListItemDto) => leaveAssistantPageIntoPanel(() => navigateToEvent(event)),
    [leaveAssistantPageIntoPanel, navigateToEvent],
  );

  const lookupEntity = useCallback((id: string) => entityIndex.current.get(id), []);

  // Same routing rules the planner's own "navigate" tool call uses (see resolveEntityNavigation),
  // exposed so an entity highlight card can send the user straight to that contract/event on click.
  const navigateToEntity = useCallback(
    (id: string) => resolveEntityNavigation(id, { entityIndex: entityIndex.current, navigate, navigateToEvent })?.(),
    [navigate, navigateToEvent],
  );

  const canOpenEntity = useCallback(
    (id: string) =>
      resolveEntityNavigation(id, { entityIndex: entityIndex.current, navigate, navigateToEvent }) !== null,
    [navigate, navigateToEvent],
  );

  const messagesRef = useRef<WireMessage[]>([]);

  const openDocument = useCallback(
    async (documentId: string, name: string): Promise<{ result: ToolResult; source: DocumentSource | null }> => {
      if (!documentId.trim()) {
        return {
          result: { ok: false, deviationCode: "MISSING_DOCUMENT_ID", message: "Mangler documentId." },
          source: null,
        };
      }
      const subscription = dispatch(
        bffApi.endpoints.getDownloadLink.initiate({ docId: documentId }, { forceRefetch: true }),
      );
      try {
        const { url } = await subscription.unwrap();
        return {
          result: { ok: true, data: { note: "Dokumentet ligger klart som kort under svaret." } },
          source: { documentId, documentName: name.trim() || "Dokument", downloadUrl: url, pages: [] },
        };
      } catch (err) {
        return { result: toError(err), source: null };
      } finally {
        subscription.unsubscribe();
      }
    },
    [dispatch],
  );

  const generateDraft = useCallback<GenerateDraft>(async (args, signal) => {
    const kind = String(args.kind ?? "general");
    const instructions = String(args.instructions ?? args.instruction ?? "");
    if (!instructions.trim()) {
      return { ok: false, deviationCode: "MISSING_INSTRUCTION", message: "Mangler instruksjon for utkastet." };
    }
    const messages: WireMessage[] = [
      { role: "system", content: buildDraftSystemPrompt(kind) },
      { role: "user", content: instructions },
    ];
    try {
      const { text, complete } = await streamCompletion(
        messages,
        selectedModelRef.current ?? undefined,
        readsConversationFiles(messagesRef.current),
        signal,
      );
      return { ok: true, data: { draft: text, complete } };
    } catch {
      return { ok: false, deviationCode: "DRAFT_FAILED", message: "Klarte ikke å lage utkast." };
    }
  }, []);

  const recordAudit = useAgentAudit();

  const [turns, setTurns] = useState<AgentTurn[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [activeStep, setActiveStep] = useState<string | null>(null);

  // Persistence activates only once the user actually opens the assistant (a panel toggle or the
  // assistant page), so we never fetch conversations for someone who never touches it.
  const [activated, setActivated] = useState(false);
  useEffect(() => {
    if (panelOpen || location.pathname === urls.app.assistant) {
      setActivated(true);
    }
  }, [panelOpen, location.pathname]);

  const listQuery = bffApi.useListAgentConversationsQuery({ limit: CONVERSATION_PAGE_SIZE }, { skip: !activated });
  const [older, setOlder] = useState<OlderConversations | null>(null);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const newestPage = listQuery.data?.conversations ?? NO_CONVERSATIONS;
  const nextOlderCursor = older ? older.nextBefore : (listQuery.data?.nextBefore ?? null);
  const showOlderConversations = useCallback(async () => {
    if (nextOlderCursor == null || isLoadingOlder) return;
    setIsLoadingOlder(true);
    try {
      const page = await listOlderConversations(dispatch, nextOlderCursor, CONVERSATION_PAGE_SIZE);
      setOlder((current) => withOlderPage(current, newestPage, page));
    } catch (error) {
      triggerApiError(error);
    } finally {
      setIsLoadingOlder(false);
    }
  }, [dispatch, nextOlderCursor, isLoadingOlder, triggerApiError, newestPage]);
  const modelsQuery = bffApi.useGetAgentModelsQuery({}, { skip: !activated });
  const assistantNotIncluded = modelsQuery.isError && isAssistantNotIncluded(modelsQuery.error);
  const allowsFency = useAllowsFency();
  const aiNotAllowed = modelsQuery.isError && isAiNotAllowed(modelsQuery.error);
  const toolsFailed = modelsQuery.isError && !assistantNotIncluded && !aiNotAllowed;
  const toolsLoading = modelsQuery.isLoading;
  const offeredByServer = useMemo(
    () => (toolsFailed ? new Set(allToolNames) : enabledToolNames(modelsQuery.data?.enabledTools)),
    [toolsFailed, modelsQuery.data],
  );
  const withheldByAiSetting = useMemo(() => {
    const withheld = new Set(modelsQuery.data?.toolsWithheldByAiSetting ?? []);
    if (!allowsFency && offeredByServer.has(SEARCH_TOOL_NAME)) withheld.add(SEARCH_TOOL_NAME);
    return withheld;
  }, [modelsQuery.data, allowsFency, offeredByServer]);
  const enabledTools = useMemo(
    () => new Set([...offeredByServer].filter((name) => !withheldByAiSetting.has(name))),
    [offeredByServer, withheldByAiSetting],
  );
  const skillLabel = useAiSkillLabels();
  const addOnSkills = useMemo(
    () => (modelsQuery.data?.addOnModules ?? []).map(skillLabel),
    [modelsQuery.data, skillLabel],
  );
  const customContractFields = useMemo(
    () => filterableCustomContractFields(authState.selectedOrg.customContractSections),
    [authState.selectedOrg.customContractSections],
  );
  const wireTools = useMemo(
    () =>
      wireToolSchemas(enabledTools).map((t) => ({
        name: t.name,
        description: t.description,
        parameters: JSON.stringify(t.parameters),
      })),
    [enabledTools],
  );
  const refetchModels = modelsQuery.refetch;
  const [storedModelId, setSelectedModelId] = useState<string | null>(null);
  const selectedModelId = offeredModelOrDefault(storedModelId, modelsQuery.data?.models);
  const selectedModelRef = useRef<string | null>(null);
  selectedModelRef.current = selectedModelId;
  const summaries = useMemo(
    () =>
      newestFirst(listQuery.data?.conversations ?? [], older?.conversations ?? []),
    [listQuery.data, older],
  );

  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [subject, setSubject] = useState<ConversationSubject | null>(null);
  const subjectRef = useRef<ConversationSubject | null>(null);
  const bindsFirstTurnToPage = useRef(false);
  const changeSubject = useCallback((next: ConversationSubject | null) => {
    subjectRef.current = next;
    setSubject(next);
  }, []);
  const bindToPage = useCallback(() => {
    bindsFirstTurnToPage.current = true;
  }, []);

  // Refs mirror the render state so a debounced save (or a save fired just before we clear state on
  // switch/new) reads the values captured at call time, never a stale closure.
  const turnsRef = useRef<AgentTurn[]>([]);
  turnsRef.current = turns;
  const activeIdRef = useRef<string | null>(null);
  activeIdRef.current = activeConversationId;
  // Bumped whenever we swap to another (or a fresh) conversation, so an in-flight save that started
  // under the previous conversation can't adopt its new id back onto the current one.
  const generationRef = useRef(0);

  const saveTriggers = useMemo<ConversationSaveTriggers>(
    () => ({
      create: (title) => createConversation(dispatch, title),
      update: (id, update) => updateConversation(dispatch, id, update),
    }),
    [dispatch],
  );
  const inFlightSaveTriggers = useMemo<ConversationSaveTriggers>(
    () => ({
      create: (title) => createConversation(dispatch, title),
      update: (id, update) => updateConversationWithoutListRefresh(dispatch, id, update),
    }),
    [dispatch],
  );

  // Only one create call may be in flight for a still-id-less conversation at a time — a concurrent
  // persist() (debounced save racing a switchTo/startNew) would otherwise each see activeIdRef as null
  // and both call create(), orphaning a duplicate. The loser just skips its save; last-write-wins
  // means the next settlement (or the winner's own save, already in flight) covers it.
  const isCreatingRef = useRef(false);

  // Save the current conversation's stored memory array exactly as-is (nothing ephemeral leaks in)
  // plus the client-side turn/entity state. Failures are swallowed and logged; the next turn's
  // settlement reschedules a retry. Never blocks the chat.
  const saveQueue = useRef<Promise<void>>(Promise.resolve());
  const savedSignatureRef = useRef<string | null>(null);
  const takeSnapshotRef = useRef<(turnInFlight: boolean) => SaveSnapshot | null>(() => null);
  const saveSnapshotRef = useRef<(snapshot: SaveSnapshot) => Promise<void>>(async () => undefined);
  const persist = useCallback((options: { turnInFlight?: boolean } = {}) => {
    const snapshot = takeSnapshotRef.current(options.turnInFlight ?? false);
    if (!snapshot) return saveQueue.current;
    const queued = saveQueue.current.then(() => saveSnapshotRef.current(snapshot));
    saveQueue.current = queued;
    return queued;
  }, []);
  takeSnapshotRef.current = (turnInFlight: boolean) => {
    if (messagesRef.current.length === 0 && turnsRef.current.length === 0) {
      return null;
    }
    const settled = {
      messages: messagesRef.current,
      clientState: buildClientState(
        turnsRef.current,
        entityIndex.current,
        selectedModelRef.current ?? undefined,
        subjectRef.current,
      ),
    };
    const { messages, clientState } = turnInFlight
      ? snapshotOfTurnInFlight(settled.messages, settled.clientState)
      : settled;
    const signature = conversationSignature(messages, clientState);
    return {
      conversationId: activeIdRef.current,
      generation: generationRef.current,
      turnInFlight,
      messages,
      clientState,
      signature,
      unchanged: activeIdRef.current !== null && signature === savedSignatureRef.current,
    };
  };
  saveSnapshotRef.current = async (snapshot: SaveSnapshot) => {
    const sameConversation = generationRef.current === snapshot.generation;
    const conversationId = snapshot.conversationId ?? (sameConversation ? activeIdRef.current : null);
    if (snapshot.unchanged) {
      return;
    }
    const isFreshCreate = conversationId === null;
    if (isFreshCreate) {
      if (isCreatingRef.current) {
        return;
      }
      isCreatingRef.current = true;
    }
    try {
      await saveConversation(
        snapshot.turnInFlight ? inFlightSaveTriggers : saveTriggers,
        { activeConversationId: conversationId, messages: snapshot.messages, clientState: snapshot.clientState },
        (id) => {
          if (generationRef.current === snapshot.generation) {
            activeIdRef.current = id;
            setActiveConversationId(id);
          }
        },
      );
      if (generationRef.current === snapshot.generation) {
        savedSignatureRef.current = snapshot.signature;
      }
    } catch (error) {
      ddRum.addError(error, { source: "assistant-conversation-save" });
    } finally {
      if (isFreshCreate) {
        isCreatingRef.current = false;
      }
    }
  };

  const applyConversation = useCallback((dto: AgentConversationDto) => {
    generationRef.current += 1;
    const { messages } = toResumableConversation(dto.messages);
    messagesRef.current = messages;
    const clientState = parseClientState(dto.clientState);
    const restoredIndex = restoreEntityIndex(clientState.entityIndex);
    const restoredTurns = clientState.turns.map(toRestoredTurn).map((turn) => ({
      ...turn,
      answeredOn: withKnownName(turn.answeredOn, (id) => indexedEntityTitle(restoredIndex.get(id))),
    }));
    setTurns(restoredTurns);
    entityIndex.current = restoredIndex;
    setSelectedModelId(clientState.model ?? null);
    bindsFirstTurnToPage.current = false;
    changeSubject(clientState.subject ?? null);
    savedSignatureRef.current = conversationSignature(
      messages,
      buildClientState(restoredTurns, entityIndex.current, clientState.model ?? undefined, clientState.subject),
    );
    setActiveConversationId(dto.id);
    activeIdRef.current = dto.id;
  }, [changeSubject]);

  const loadConversation = useCallback(
    async (id: string) => applyConversation(await getConversation(dispatch, id)),
    [applyConversation, dispatch],
  );

  const resetToNewConversation = useCallback(() => {
    generationRef.current += 1;
    savedSignatureRef.current = null;
    messagesRef.current = [];
    entityIndex.current = new Map();
    setTurns([]);
    setSelectedModelId(null);
    bindsFirstTurnToPage.current = false;
    changeSubject(null);
    setActiveConversationId(null);
    activeIdRef.current = null;
    if (activated) {
      void refetchModels();
    }
  }, [activated, refetchModels, changeSubject]);

  // Hydrate once, from the newest conversation, the first time the assistant is opened. Bails if the
  // user already started a turn before the initial list+get resolved — loading the newest saved
  // conversation over an in-flight turn would silently clobber messagesRef with no way to recover it.
  const hydratedRef = useRef(false);
  const [restored, setRestored] = useState(false);
  useEffect(() => {
    if (!activated || hydratedRef.current || !listQuery.isSuccess || isRunning || messagesRef.current.length > 0) {
      return;
    }
    hydratedRef.current = true;
    const newest = summaries[0];
    if (newest) {
      void loadConversation(newest.id)
        .catch((error) => ddRum.addError(error, { source: "assistant-conversation-load" }))
        .finally(() => setRestored(true));
    } else {
      setRestored(true);
    }
  }, [activated, listQuery.isSuccess, summaries, loadConversation, isRunning]);

  const switchTo = useCallback(
    async (id: string) => {
      if (isRunning || id === activeIdRef.current) {
        return;
      }
      await persist();
      await loadConversation(id);
      void refetchModels();
    },
    [isRunning, persist, loadConversation, refetchModels],
  );

  const startNew = useCallback(() => {
    if (isRunning) {
      return;
    }
    void persist();
    resetToNewConversation();
  }, [isRunning, persist, resetToNewConversation]);

  const [previousConversation, setPreviousConversation] = useState<{ id: string; title: string } | null>(null);
  const openConversation = useCallback(() => {
    const open = summaries.find((conversation) => conversation.id === activeIdRef.current);
    return open ? { id: open.id, title: open.title } : null;
  }, [summaries]);
  const { startsNewWhenAnswered } = usePageRequestStartsConversation({
    pageRequest,
    page: `${location.pathname}${location.search}`,
    hasTurns: turns.length > 0,
    isRunning,
    isRestoring: !restored && !listQuery.isError && turns.length === 0,
    openConversation,
    startNew,
    bindToPage,
    onLeft: setPreviousConversation,
  });
  useEffect(() => {
    if (turns.length > 0) setPreviousConversation(null);
  }, [turns.length]);
  const returnToPrevious = useCallback(
    async () => (previousConversation ? switchTo(previousConversation.id) : undefined),
    [previousConversation, switchTo],
  );

  useHandoffStartsConversation({
    handoff,
    hasTurns: turns.length > 0,
    isRunning,
    isRestoring: !restored && !listQuery.isError && turns.length === 0,
    startNew,
  });

  const remove = useCallback(
    async (id: string) => {
      if (isRunning) {
        return;
      }
      await deleteConversation(dispatch, id);
      setOlder((current) =>
        current && { ...current, conversations: current.conversations.filter((conversation) => conversation.id !== id) },
      );
      if (id === activeIdRef.current) {
        resetToNewConversation();
      }
    },
    [isRunning, dispatch, resetToNewConversation],
  );

  const lastInFlightSaveAt = useRef(0);
  useEffect(() => {
    if (!isRunning) {
      return;
    }
    const wait = Math.max(0, lastInFlightSaveAt.current + IN_FLIGHT_SAVE_INTERVAL_MS - Date.now());
    const handle = setTimeout(() => {
      lastInFlightSaveAt.current = Date.now();
      void persist({ turnInFlight: true });
    }, wait);
    return () => clearTimeout(handle);
  }, [isRunning, turns, persist]);

  const [settledAt, setSettledAt] = useState(0);
  useEffect(() => {
    if (settledAt === 0) {
      return;
    }
    const handle = setTimeout(() => void persist(), SAVE_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [settledAt, persist]);

  const [pendingConfirmation, setPendingConfirmation] = useState<PendingConfirmation | null>(null);
  const [queuedMessage, setQueuedMessage] = useState<{
    text: string;
    startButtonLabel?: string;
    attachments?: ConversationAttachment[];
  } | null>(null);
  const confirmResolver = useRef<((decision: ConfirmDecision) => void) | null>(null);

  const waitForConfirmation = useCallback((pending: PendingConfirmation): Promise<ConfirmDecision> => {
    setPendingConfirmation(pending);
    setActiveStep(null);
    return new Promise<ConfirmDecision>((resolve) => {
      confirmResolver.current = resolve;
    });
  }, []);

  const settle = useCallback((decision: ConfirmDecision) => {
    setPendingConfirmation(null);
    const resolve = confirmResolver.current;
    confirmResolver.current = null;
    resolve?.(decision);
  }, []);

  const confirm = useCallback(
    (finalArgs: Record<string, unknown>, values?: AuditedValues) => settle({ kind: "confirm", finalArgs, values }),
    [settle],
  );

  const cancel = useCallback(() => settle({ kind: "cancel" }), [settle]);

  const refine = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || !confirmResolver.current) {
        return;
      }
      setQueuedMessage({ text: trimmed });
      settle({ kind: "supersede" });
    },
    [settle],
  );


  const currentPageContext = useCallback(() => {
    if (documentScopeRef.current) return documentScopeContext(documentScopeRef.current);
    const lookups = {
      entityLabel: (id: string) => pageEntityName(id) ?? (entityLabel(entityIndex.current.get(id)) || undefined),
      wizardStep,
      creationProgress: creationProgressNow,
    };
    const { pathname, search } = turnPageRef.current?.address ?? locationRef.current;
    const pageContext = resolvePageContext(pathname, search, lookups);
    const awaySubject = subjectAwayFromPage(subjectRef.current, pageContext?.entity);
    return awaySubject ? conversationSubjectContext(awaySubject, lookups) : pageContext;
  }, []);

  const bindFirstTurnToPage = useCallback(() => {
    if (!bindsFirstTurnToPage.current) return;
    bindsFirstTurnToPage.current = false;
    if (documentScopeRef.current || subjectRef.current) return;
    const entity = currentPageContext()?.entity;
    if (entity) changeSubject(subjectOfPage(entity, pageEntityName(entity.id)));
  }, [currentPageContext, changeSubject]);

  const answeredOnNow = useCallback((): AnsweredOn | undefined => {
    const entity = currentPageContext()?.entity;
    if (!entity) return undefined;
    const name =
      pageEntityName(entity.id) ??
      indexedEntityTitle(entityIndex.current.get(entity.id)) ??
      (subjectRef.current?.id === entity.id ? subjectRef.current.name : undefined);
    return { kind: entity.kind, id: entity.id, ...(name ? { name } : {}) };
  }, [currentPageContext]);

  const { createDocSearchSession } = useAgentSession();
  const currentPageEntity = useCallback(() => currentPageContext()?.entity ?? null, [currentPageContext]);
  const { hasFencyContractSearchAccess } = useSub();
  const searchDocuments = useDocumentSearch({
    createDocSearchSession,
    currentPageEntity,
    searchesWholeArchive: hasFencyContractSearchAccess,
  });

  const stopRequested = useRef(false);
  const turnAbort = useRef<AbortController | null>(null);
  const [stopping, setStopping] = useState(false);
  const stop = useCallback(() => {
    stopRequested.current = true;
    setStopping(true);
    turnAbort.current?.abort();
  }, []);

  const progressMayBeStale = useRef(false);
  const progressAfterSaves = useCallback(async () => {
    if (!progressMayBeStale.current) return;
    progressMayBeStale.current = false;
    const refreshed = refreshCreationProgress().catch((error: unknown) => {
      ddRum.addError(error, { source: "assistant-progress-refresh" });
    });
    await Promise.race([refreshed, settlesAfter(PROGRESS_REFRESH_TIMEOUT_MS, turnAbort.current?.signal)]);
  }, []);

  const planStream = useCallback(
    async (handlers: StreamHandlers) => {
      await progressAfterSaves();
      return streamAgentTurn(
        {
          messages: withPageContext(messagesRef.current, currentPageContext()),
          tools: wireTools,
          model: selectedModelRef.current ?? undefined,
        },
        handlers,
        { idleTimeoutMs: PLAN_IDLE_TIMEOUT_MS, signal: turnAbort.current?.signal },
      );
    },
    [wireTools, currentPageContext, progressAfterSaves],
  );

  const toolLabel = useCallback(
    (requestedName: string) => stepLabelFor(currentToolName(requestedName)),
    [],
  );

  const send = useCallback(
    async (text: string, startButtonLabel?: string, attachments: ConversationAttachment[] = []) => {
      const trimmed = text.trim();
      if ((!trimmed && attachments.length === 0) || isRunning) {
        return;
      }

      const turnIndex = turns.length;
      const updateTurn = (patch: (turn: AgentTurn) => AgentTurn) =>
        setTurns((prev) => prev.map((turn, i) => (i === turnIndex ? patch(turn) : turn)));
      const turnPage: TurnPage = {
        address: addressOf(locationRef.current),
        onAssistantMove: () => {
          const movedTo = answeredOnNow();
          updateTurn((turn) => ({ ...turn, answeredOn: movedTo }));
        },
      };
      turnPageRef.current = turnPage;
      if (turnIndex === 0) bindFirstTurnToPage();
      const answeredOn = answeredOnNow();

      setTurns((prev) => [
        ...prev,
        {
          user: trimmed,
          steps: [],
          answerText: null,
          note: null,
          ...(startButtonLabel ? { startButtonLabel } : {}),
          ...(attachments.length ? { attachments } : {}),
          ...(answeredOn ? { answeredOn } : {}),
        },
      ]);
      setIsRunning(true);
      lastInFlightSaveAt.current = 0;
      setActiveStep(null);
      stopRequested.current = false;
      turnAbort.current = new AbortController();
      const toolNames = wireTools.map((tool) => tool.name);
      const onAnswerDelta = (accumulated: string) => {
        setActiveStep(null);
        updateTurn((turn) => ({ ...turn, answerText: withoutToolNames(accumulated, toolNames) }));
      };

      const ctx: ToolContext = {
        dispatch,
        auth: {
          organizationId,
          personId,
          departmentId,
          eventDepartments,
          contractDepartments,
          projectDepartments,
          defaultProjectDepartmentId,
        },
        defaultCountryId: norwayCountryId,
        buyerRunsPublicProcurements,
        buyerRegulation,
        newProcurementProjectId:
          turnPage.address.pathname === urls.app.assistant
            ? projectOfNewProcurement(turnPage.address.search)
            : undefined,
        createsProcurementWithAi: createsProcurementWithAi(turnPage.address),
        regulationPending: () => regulationPendingRef.current,
      };
      const callDeps: CallDeps = {
        ctx,
        entityIndex: entityIndex.current,
        navigate: assistantNavigate,
        navigateToEvent: assistantNavigateToEvent,
        searchDocuments,
        openDocument,
        generateDraft,
        signal: turnAbort.current.signal,
        recordAudit,
        waitForConfirmation,
        setActiveStep,
        updateTurn,
        turnIndex,
        userInstruction: trimmed,
        seenCalls: new Set<string>(),
        refusedProposals: [],
        refusedUserValues: [],
        missingValues: [],
        askedAboutRemovals: [],
        askedAboutWeightTotal: [],
        classificationsConfirmed: [],
        userWords: [...turnsRef.current.map((turn) => turn.user), trimmed].join("\n"),
        enabledTools: toolsFailed ? null : enabledTools,
        withheldByAiSetting,
      };

      const historyBeforeTurn = settleHistoryForTurn(messagesRef.current);
      const memory = createAgentMemory(messagesRef.current);
      memory.initSystem(
        buildSystemPrompt({
          organizationId,
          personId,
          eventDepartments,
          contractDepartments,
          projectDepartments,
          addOnSkills,
          customContractFields,
          searchesWholeArchive: hasFencyContractSearchAccess,
          buyerRunsPublicProcurements,
          now: new Date(),
        }),
      );
      let changedSomething = false;
      let failedWithNothingChanged = false;
      const progress = emptyTurnProgress();
      const endStopped = (written: string | null) => {
        const shown = written?.trim() ? withoutDanglingMarkdown(withoutToolNames(written, toolNames)) || null : null;
        const summary = stopSummary(progress, shown !== null);
        memory.endReplyWith(summary);
        updateTurn((turn) => ({ ...turn, answerText: shown ? `${shown}\n\n${summary}` : summary, note: null }));
      };
      const markFailed = () => {
        failedWithNothingChanged = !changedSomething;
        updateTurn((turn) => ({
          ...turn,
          note: PLAN_FAILED_NOTE,
          failed: true,
          retryable: failedWithNothingChanged,
          changedSomething,
        }));
      };
      try {
        const result = takeHandoff?.();
        memory.setAsideEarlierFileReads();
        memory.dropFabricatedTurns();
        const typed = withAttachments(trimmed, attachments);
        memory.pushUser(result ? withAiResult(typed, result) : typed);

        const { outcome, plannerReply, toolContext, usedToolNames, lists, cutOff, endedBy } = await runTurnLoop({
          planStream,
          executeCall: (name, args, plannerSummary) => executeCall(name, args, callDeps, plannerSummary),
          memory,
          maxSteps: MAX_STEPS,
          shouldStop: () => stopRequested.current,
          fitsNextPlan: () => fitsPlanBudget(messagesRef.current),
          progress,
          onFabricatedTurn: (droppedToolCalls) =>
            ddRum.addError(new Error("The planner wrote a turn for the user"), {
              source: "assistant-fabricated-turn",
              droppedToolCalls,
            }),
          cardCorrection: ctx.createsProcurementWithAi
            ? (answer) => (endsWithQuestionWithoutCard(answer) ? CARD_CORRECTION : null)
            : undefined,
          statusLabel,
          setActiveStep,
          onPlannerText: onAnswerDelta,
          onToolCallStart: (name) => {
            if (changesData(name)) {
              changedSomething = true;
              progressMayBeStale.current = true;
            }
            setActiveStep(toolLabel(name));
          },
          onPlanError: (err) => {
            if (isAssistantNotIncluded(err)) {
              updateTurn((turn) => ({ ...turn, note: ASSISTANT_NOT_INCLUDED_NOTE }));
              void refetchModels();
              return;
            }
            if (isContentFiltered(err)) {
              const scoped = documentScopeRef.current?.name;
              const names = [...(scoped ? [scoped] : []), ...attachments.map((file) => file.fileName)];
              updateTurn((turn) => ({ ...turn, note: contentFilteredNote(names) }));
              failedWithNothingChanged = !changedSomething;
              return;
            }
            if (isAiNotAllowed(err)) {
              const note = aiSettingRefusalNote(attachments, messagesRef.current.slice(historyBeforeTurn));
              updateTurn((turn) => ({ ...turn, note }));
              failedWithNothingChanged = !changedSomething;
              void refetchModels();
              return;
            }
            ddRum.addError(err, { source: "assistant-answer" });
            markFailed();
          },
        });

        if (lists.length) {
          updateTurn((turn) => ({ ...turn, lists }));
        }
        if (endedBy === "stopped" || (stopRequested.current && outcome === "stream")) {
          endStopped(outcome === "answered" ? plannerReply : null);
        } else if (outcome === "answered") {
          updateTurn((turn) => ({
            ...turn,
            answerText: plannerReply === null ? null : withoutToolNames(plannerReply, toolNames),
          }));
          if (cutOff) {
            markFailed();
          }
        } else if (outcome === "stream") {
          setActiveStep("Skriver svar");
          await progressAfterSaves();
          if (stopRequested.current) {
            endStopped(null);
          } else {
            const answer = await streamAnswer({
              question: typed,
              toolContext,
              usedToolNames,
              toolNames,
              plannerReply,
              endedBy,
              memory,
              earlier: messagesRef.current.slice(0, historyBeforeTurn),
              pageContext: currentPageContext()?.description ?? null,
              model: selectedModelRef.current ?? undefined,
              idleTimeoutMs: ANSWER_IDLE_TIMEOUT_MS,
              stream: streamAgentTurn,
              onDelta: onAnswerDelta,
              signal: turnAbort.current?.signal,
            });
            if (answer.cutOff && stopRequested.current) {
              endStopped(answer.text);
            } else {
              updateTurn((turn) => ({ ...turn, answerText: answer.text }));
              if (answer.cutOff) markFailed();
            }
          }
        }
      } catch (error) {
        if (stopRequested.current) {
          endStopped(null);
        } else {
          ddRum.addError(error, { source: "assistant-answer" });
          markFailed();
        }
      } finally {
        turnPageRef.current = null;
        updateTurn((turn) => ({ ...turn, answeredOn: withKnownName(turn.answeredOn, pageEntityName) }));
        if (failedWithNothingChanged) {
          messagesRef.current.length = historyBeforeTurn;
        }
        setActiveStep(null);
        setStopping(false);
        setIsRunning(false);
        setSettledAt((n) => n + 1);
      }
    },
    [
      isRunning,
      turns,
      planStream,
      progressAfterSaves,
      wireTools,
      toolLabel,
      recordAudit,
      waitForConfirmation,
      dispatch,
      assistantNavigate,
      assistantNavigateToEvent,
      organizationId,
      personId,
      departmentId,
      searchDocuments,
      generateDraft,
      openDocument,
      norwayCountryId,
      buyerRunsPublicProcurements,
      buyerRegulation,
      statusLabel,
      enabledTools,
      addOnSkills,
      customContractFields,
      toolsFailed,
      refetchModels,
      takeHandoff,
      bindFirstTurnToPage,
      answeredOnNow,
    ],
  );

  const retry = useCallback(() => {
    const failed = turns[turns.length - 1];
    if (isRunning || !failed?.retryable) {
      return;
    }
    setTurns((prev) => prev.slice(0, -1));
    setQueuedMessage({ text: failed.user, startButtonLabel: failed.startButtonLabel, attachments: failed.attachments });
  }, [isRunning, turns]);

  useEffect(() => {
    if (!isRunning && queuedMessage !== null) {
      const next = queuedMessage;
      setQueuedMessage(null);
      void send(next.text, next.startButtonLabel, next.attachments);
    }
  }, [isRunning, queuedMessage, send]);

  useAskedQuestionIsSent({
    question,
    hasTurns: turns.length > 0,
    isRunning,
    isRestoring: !restored && !listQuery.isError && turns.length === 0,
    startNew,
    send,
  });

  return {
    turns,
    isRunning,
    stop,
    stopping,
    activeStep: stopping ? "Stopper" : activeStep,
    pendingConfirmation,
    confirm,
    cancel,
    refine,
    lookupEntity,
    navigateToEntity,
    canOpenEntity,
    send,
    retry,
    summaries,
    isLoadingConversations: listQuery.isLoading,
    conversationsFailed: listQuery.isError,
    retryConversations: listQuery.refetch,
    hasOlderConversations: nextOlderCursor != null,
    isLoadingOlderConversations: isLoadingOlder,
    showOlderConversations,
    activeConversationId,
    subject,
    bindToPage,
    switchTo,
    startNew,
    remove,
    previousConversation,
    returnToPrevious,
    startsNewWhenAnswered,
    isRestoring: !restored && !listQuery.isError && turns.length === 0,
    models: modelsQuery.data?.models ?? [],
    defaultModelId: modelsQuery.data?.defaultModelId ?? null,
    selectedModelId,
    selectModel: setSelectedModelId,
    enabledTools,
    withheldByAiSetting,
    toolsLoading,
    toolsReady: activated && modelsQuery.isSuccess,
    toolsSettled: activated && (modelsQuery.isSuccess || modelsQuery.isError),
    toolsFailed,
    assistantNotIncluded,
    aiNotAllowed,
    retryTools: refetchModels,
    setRegulationPending,
  };
};
